import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useApolloClient } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import { Button, Tag, Text, Textarea, useToastContext } from "@ttc3k/trekker";
import { Send } from "iconoir-react";
import { Link } from "react-router";
import { Box, Flex } from "styled-system/jsx";
import {
  WorkbenchMessageFieldsFragment,
  useWorkbenchChatByIdQuery,
  useWorkbenchChatCreateMutation,
  useWorkbenchChatSendMessageMutation,
  useWorkbenchMessagesByChatQuery,
  useWorkbenchReviewCompleteMutation,
  useWorkbenchSettingsGetQuery,
} from "gql/generated";
import { useWorkbenchSession } from "shared/contextProviders/workbenchSession";
import { ProposedEditCard } from "./ProposedEditCard";
import { WORKBENCH_REFETCH_QUERIES } from "./refetchQueries";
import { streamWorkbenchMessage, WorkbenchStreamUnavailableError } from "./streamWorkbenchMessage";
import { useTypewriter } from "./useTypewriter";
import { getAccessTokenSingleFlight } from "shared/utils/authTokenSingleFlight";
import { WhereabotAvatar } from "./WhereabotAvatar";
import { WorkbenchMarkdown } from "./WorkbenchMarkdown";

const SETTINGS_PATH = "/app/admin?view=WORKBENCH&tab=SETTINGS";

const QUICK_PROMPTS = [
  "Explain this feature",
  "Walk me through testing it",
  "What does the documentation say about setting it up?",
];


type WorkbenchChatPanelProps = {
  chatId: string;
  /** False while the navbar slideout is closed, so replies can raise the unread dot. */
  isVisible?: boolean;
  onReviewCompleted?: () => void;
  /**
   * When provided, a finished review shows a "Start new chat" button. Called with the new chat's id
   * so the parent can swap it in. Omit it where new chats aren't allowed (e.g. expelled features).
   */
  onNewChatStarted?: (chatId: string) => void;
};

const SystemLine = ({ message }: { message: WorkbenchMessageFieldsFragment }) => (
  <Text visual="smallRegular" color="text.light" textAlign="center" px="300">
    {message.content}
  </Text>
);

const MessageBubble = ({ message }: { message: WorkbenchMessageFieldsFragment }) => {
  const isUser = message.role === "user";
  const eventCount = message.sessionEvents?.length ?? 0;
  return (
    <Flex flexDir="column" gap="100" alignItems={isUser ? "flex-end" : "flex-start"}>
      <Flex gap="100" alignItems="flex-start" justifyContent={isUser ? "flex-end" : "flex-start"} w="full">
        {!isUser && <WhereabotAvatar size={28} />}
        <Box
          px="200"
          py="150"
          borderRadius="200"
          maxW="88%"
          bg={isUser ? "bg.accent.blue.lightest" : message.isError ? "bg.error.lightest" : "bg.wa.light"}
        >
          {isUser ? (
            <Text visual="smallRegular" whiteSpace="pre-wrap">
              {message.content}
            </Text>
          ) : (
            <WorkbenchMarkdown variant="chat">{message.content}</WorkbenchMarkdown>
          )}
        </Box>
      </Flex>
      {!isUser && message.metadata?.model && (
        <Text visual="smallRegular" color="text.light" pl="400" fontSize="xs">
          via {message.metadata.model}
        </Text>
      )}
      {isUser && eventCount > 0 && (
        <Tag visual="primary" mode="light">
          {eventCount} session event{eventCount === 1 ? "" : "s"} attached
        </Tag>
      )}
      {!isUser && (message.workbenchSuggestions?.length ?? 0) > 0 && (
        <Flex flexDir="column" gap="100" w="full" pl="400">
          {message.workbenchSuggestions!.map(s => (
            <ProposedEditCard key={s._id} suggestion={s} />
          ))}
        </Flex>
      )}
    </Flex>
  );
};

export const WorkbenchChatPanel = ({
  chatId,
  isVisible = true,
  onReviewCompleted,
  onNewChatStarted,
}: WorkbenchChatPanelProps) => {
  const { toastFactory } = useToastContext();
  const { drain, pendingEventCount, setHasUnread, isTesting, session } = useWorkbenchSession();
  const [input, setInput] = useState("");
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(null);
  const [replying, setReplying] = useState(false);
  const [replyTarget, setReplyTarget] = useState("");
  const typed = useTypewriter(replyTarget);
  const typedLengthRef = useRef(0);
  /** Message count when the current send started; the live bubbles hide once the saved ones arrive. */
  const [replyBaseCount, setReplyBaseCount] = useState(0);
  typedLengthRef.current = typed.text.length;
  const apolloClient = useApolloClient();
  const { getAccessTokenSilently } = useAuth0();
  const [confirmFinish, setConfirmFinish] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isVisibleRef = useRef(isVisible);
  isVisibleRef.current = isVisible;

  const { data: chatData } = useWorkbenchChatByIdQuery({ variables: { _id: chatId } });
  const { data: settingsData } = useWorkbenchSettingsGetQuery({ fetchPolicy: "cache-first" });
  const chatModel = settingsData?.workbenchSettingsGet.chatModel;
  const { data, loading, refetch } = useWorkbenchMessagesByChatQuery({
    variables: { chatId, limit: 100 },
    fetchPolicy: "cache-and-network",
  });
  const [sendMessage] = useWorkbenchChatSendMessageMutation();
  const [completeReview, { loading: completing }] = useWorkbenchReviewCompleteMutation({
    refetchQueries: WORKBENCH_REFETCH_QUERIES,
  });

  const [createChat, { loading: startingChat }] = useWorkbenchChatCreateMutation({
    refetchQueries: ["WorkbenchFeature", "WorkbenchFeatureList"],
  });

  const chat = chatData?.whereabotChatById;
  const isArchived = chat?.status === "archived";
  const messages = (data?.whereabotMessagesByChat ?? []).filter(
    (m): m is WorkbenchMessageFieldsFragment => !!m,
  );
  const attachEvents = isTesting && session?.chatId === chatId;

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, pendingUserMessage, replying, typed.text]);

  /** Resolves once the typewriter has revealed `finalText`, so the saved message never "jumps" in. */
  const waitForReveal = (finalText: string) =>
    new Promise<void>(resolve => {
      const check = () =>
        typedLengthRef.current >= finalText.length ? resolve() : requestAnimationFrame(check);
      requestAnimationFrame(check);
    });

  const send = async (raw: string) => {
    const content = raw.trim();
    if (!content || replying) return;
    setInput("");
    setPendingUserMessage(content);
    setReplying(true);
    setReplyTarget("");
    setReplyBaseCount(messages.length);
    const events = attachEvents ? drain() : [];
    let finalText = "";
    try {
      const token = await getAccessTokenSingleFlight(() =>
        getAccessTokenSilently({
          authorizationParams: { audience: import.meta.env.VITE_AUTH0_AUDIENCE },
        }),
      );
      try {
        if (!token) throw new WorkbenchStreamUnavailableError("No access token");
        await streamWorkbenchMessage({
          chatId,
          content,
          sessionEvents: events.length ? events : undefined,
          token,
          onAnswer: text => {
            finalText = text;
            setReplyTarget(text);
          },
        });
      } catch (err) {
        if (!(err instanceof WorkbenchStreamUnavailableError)) throw err;
        const result = await sendMessage({
          variables: { chatId, content, sessionEvents: events.length ? events : undefined },
        });
        finalText = result.data?.workbenchChatSendMessage.content ?? "";
        setReplyTarget(finalText);
      }
      await waitForReveal(finalText);
      await refetch();
      await apolloClient.refetchQueries({ include: WORKBENCH_REFETCH_QUERIES });
      if (!isVisibleRef.current) setHasUnread(true);
    } catch (err) {
      toastFactory.error({ title: err instanceof Error ? err.message : "Message failed" });
      setInput(content);
    } finally {
      setPendingUserMessage(null);
      setReplying(false);
      setReplyTarget("");
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send(input);
    }
  };

  const finishReview = async () => {
    try {
      await completeReview({ variables: { chatId } });
      setConfirmFinish(false);
      toastFactory.success({ title: "Review finished. Your feedback is on the Plan tab." });
      onReviewCompleted?.();
    } catch (err) {
      toastFactory.error({ title: err instanceof Error ? err.message : "Could not finish the review" });
    }
  };

  const startNewChat = async () => {
    if (!chat?.featureFlagKey) return;
    try {
      const result = await createChat({
        variables: {
          featureFlagKey: chat.featureFlagKey,
          testOrganizationId: chat.testOrganizationId ?? null,
        },
      });
      const newChatId = result.data?.workbenchChatCreate._id;
      if (newChatId) onNewChatStarted?.(String(newChatId));
    } catch (err) {
      toastFactory.error({ title: err instanceof Error ? err.message : "Could not start a new chat" });
    }
  };

  return (
    <Flex flexDir="column" h="full" w="full" minH="0">
      <Flex
        ref={scrollRef}
        flex="1"
        minH="0"
        overflowY="auto"
        flexDir="column"
        gap="200"
        p="300"
      >
        {loading && messages.length === 0 && (
          <Text visual="smallRegular" color="text.light" textAlign="center">
            Loading…
          </Text>
        )}
        {!loading && messages.length === 0 && !pendingUserMessage && (
          <Flex flexDir="column" gap="150" alignItems="center" py="400">
            <WhereabotAvatar size={40} />
            <Text visual="smallRegular" color="text.mid" textAlign="center">
              Ask about the feature, report what you find, or ask me to change the
              plan or documentation.
            </Text>
            <Flex gap="100" flexWrap="wrap" justifyContent="center">
              {QUICK_PROMPTS.map(prompt => (
                <Button
                  key={prompt}
                  size="sm"
                  visual="primary"
                  mode="light"
                  width="auto"
                  disabled={isArchived}
                  onClick={() => void send(prompt)}
                >
                  {prompt}
                </Button>
              ))}
            </Flex>
          </Flex>
        )}
        {messages.map(message =>
          message.role === "system" ? (
            <SystemLine key={message._id} message={message} />
          ) : (
            <MessageBubble key={message._id} message={message} />
          ),
        )}
        {pendingUserMessage && messages.length <= replyBaseCount && (
          <Flex justifyContent="flex-end">
            <Box px="200" py="150" borderRadius="200" maxW="88%" bg="bg.accent.blue.lightest" opacity={0.7}>
              <Text visual="smallRegular" whiteSpace="pre-wrap">
                {pendingUserMessage}
              </Text>
            </Box>
          </Flex>
        )}
        {replying && !typed.text && messages.length <= replyBaseCount + 1 && (
          <Flex gap="100" alignItems="center">
            <WhereabotAvatar size={28} />
            <Text visual="smallRegular" color="text.light">
              Thinking…
            </Text>
          </Flex>
        )}
        {replying && typed.text && messages.length <= replyBaseCount + 1 && (
          <Flex gap="100" alignItems="flex-start">
            <WhereabotAvatar size={28} />
            <Box px="200" py="150" borderRadius="200" maxW="88%" bg="bg.wa.light">
              <WorkbenchMarkdown variant="chat">{typed.text}</WorkbenchMarkdown>
            </Box>
          </Flex>
        )}
      </Flex>

      {isArchived ? (
        <Box p="300" borderTopWidth="1px" borderColor="border.light" bg="bg.wa.light">
          <Text visual="smallSemiBold">Review finished</Text>
          {chat?.reviewSummary && (
            <Box mt="100">
              <WorkbenchMarkdown>{chat.reviewSummary}</WorkbenchMarkdown>
            </Box>
          )}
          {onNewChatStarted && chat?.featureFlagKey && (
            <Flex mt="200" justifyContent="flex-end">
              <Button
                size="sm"
                visual="accent.blue"
                mode="dark"
                width="auto"
                isLoading={startingChat}
                onClick={() => void startNewChat()}
              >
                Start new chat
              </Button>
            </Flex>
          )}
        </Box>
      ) : (
        <Flex flexDir="column" gap="100" p="300" borderTopWidth="1px" borderColor="border.light">
          {attachEvents && pendingEventCount > 0 && (
            <Text visual="smallRegular" color="text.light">
              {pendingEventCount} session event{pendingEventCount === 1 ? "" : "s"} will be attached
            </Text>
          )}
          <Textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask Whereabot about this feature…"
            rows={2}
            hideCharCounter
            disabled={replying}
          />
          <Flex justifyContent="flex-end" gap="100" alignItems="center">
            {chatModel && !confirmFinish && (
              <Text visual="smallRegular" color="text.light" fontSize="xs" mr="auto" truncate minW="0">
                <Link to={SETTINGS_PATH} title="Change in Workbench settings">
                  {chatModel.primary.replace(/^\w+:/, "")}
                </Link>
              </Text>
            )}
            {confirmFinish ? (
              <>
                <Text visual="smallRegular" color="text.mid">
                  Summarise this chat into feedback for the developer?
                </Text>
                <Button size="sm" visual="primary" mode="light" width="auto" onClick={() => setConfirmFinish(false)}>
                  Not yet
                </Button>
                <Button size="sm" visual="success" mode="dark" width="auto" isLoading={completing} onClick={finishReview}>
                  Finish review
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                visual="primary"
                mode="light"
                width="auto"
                flexShrink={0}
                whiteSpace="nowrap"
                disabled={messages.filter(m => m.role === "user").length === 0}
                onClick={() => setConfirmFinish(true)}
              >
                Finish review
              </Button>
            )}
            {!confirmFinish && (
              <Button
                size="sm"
                visual="accent.blue"
                mode="dark"
                width="auto"
                flexShrink={0}
                whiteSpace="nowrap"
                Icon={Send}
                isIconRightAligned
                disabled={!input.trim() || replying}
                onClick={() => void send(input)}
              >
                Send
              </Button>
            )}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
